/**
 * Diner-facing copy for a rejected promo code, keyed by the checkout response's
 * `additional_data.reason` — TEAM-14743. The third sibling of `@/constants/fpoErrorCopy` (keyed by
 * the numeric Chowly FPO error code) and `@/constants/errorSlugCopy` (keyed by coo-api's semantic
 * `error` slug). See `resolveDinerMessage` (`@/utils/fpoErrors`) for how a reason becomes the
 * sentence a diner reads — this catalog is the render rule's top tier, above the numeric code.
 *
 * ## Why key on the reason and not the code
 *
 * chowly-api (TEAM-14726) refuses a promo code with one of seven typed rejections and carries the
 * machine reason in `additional_data.reason`; every one answers the diner with the same generic
 * `detail` ("The discount code is not valid.") on purpose, so the reason — not the wording — is the
 * only signal that tells them apart. Those rejections arrive under chowly-api codes 39-45, which
 * `FPO_ERROR_COPY` deliberately does not catalogue (it mirrors coo-api's enum only through 36), so
 * keying copy on the code would resolve nothing and trip the FPO drift detector on every rejection.
 * Keying on the reason is what this ticket does — see docs/DINER_ERROR_MESSAGES.md.
 *
 * ## The two rules the FPO catalog states, restated because they apply identically
 *
 * 1. **Copy lives here, in code — not in the per-brand Strings config.** Failure copy should not
 *    vary by brand: support recognises the message a customer describes and QA verifies one string.
 *    Do **not** author into `packages/sdk/src/types/gen/strings.fallback.ts`.
 * 2. **There is no same-PR sync obligation with the backends.** A reason chowly-api adds later that
 *    is missing here resolves to `PROMO_REJECTION_FALLBACK_COPY` — never blank — and needs no drift
 *    warning: `usage_limit_reached` is exactly that case today (chowly-api emits it, Product has not
 *    yet approved copy), and it renders the fallback rather than an engineer-invented sentence.
 *
 * @see resolveDinerMessage in `@/utils/fpoErrors` — the canonical render rule
 * @see docs/DINER_ERROR_MESSAGES.md
 */

/**
 * The `additional_data.reason` values this catalog carries copy for. The strings are the wire
 * contract — verified against chowly-api's merged `DiscountCodeRejection::REASONS` (PR #2684). Six
 * are reasons chowly-api emits today; `WrongLocation` is Product-approved copy for a reason the wire
 * does not emit yet, kept so the branch is ready the moment it does (`error_type_for` there is the
 * backend's equivalent forward slot).
 */
export const PROMO_REJECTION_REASON = {
  NotFound: 'not_found',
  Expired: 'expired',
  NotYetActive: 'not_yet_active',
  WrongLocation: 'wrong_location',
  ValidationUnavailable: 'validation_unavailable',
  BelowBasketMinimum: 'below_basket_minimum',
  RetryLimitReached: 'retry_limit_reached',
} as const;

/** A reason **this catalog knows**. Every one of these has a `PROMO_REJECTION_COPY` entry. */
export type PromoRejectionReason =
  (typeof PROMO_REJECTION_REASON)[keyof typeof PROMO_REJECTION_REASON];

/**
 * Product-approved copy per reason (customfield_10110, resolved 2026-09-23). Verbatim — including
 * `retry_limit_reached`'s deliberate lack of a closing period, which is why this catalog is not
 * bound by `fpoErrorCopy.ts`'s "every sentence ends cleanly" constraint. The toast-safety
 * constraints (no `<`/`&`, non-blank, under length) still apply and are pinned in
 * `constants/__tests__/promoRejectionCopy.ts`.
 */
export const PROMO_REJECTION_COPY: Record<PromoRejectionReason, string> = {
  [PROMO_REJECTION_REASON.NotFound]:
    "We couldn't find that promo code. Double-check for typos and try again.",
  [PROMO_REJECTION_REASON.Expired]: 'This promo code has expired and can no longer be used.',
  [PROMO_REJECTION_REASON.NotYetActive]: "This promo code isn't active yet.",
  [PROMO_REJECTION_REASON.WrongLocation]:
    "This promo code isn't valid at this location. Check with the restaurant for codes that work here.",
  [PROMO_REJECTION_REASON.ValidationUnavailable]:
    'Try again, try another code, or reach out to the restaurant.',
  [PROMO_REJECTION_REASON.BelowBasketMinimum]:
    'This code requires a higher order total. Add more items and try again.',
  [PROMO_REJECTION_REASON.RetryLimitReached]:
    'Too many attempts. Please wait a few minutes and try again',
};

/**
 * Tier-0's never-blank fallback — shown for a rejection whose reason is present but not one of the
 * seven above (customfield_10110's eighth AC). Deliberately the same sentence `FPO_ERROR_COPY`
 * gives code 23 (`InvalidDiscountCode`), so the two generic paths read identically to a diner: a
 * rejection with no reason at all falls through tier 0 to code 23, and a rejection with an unmapped
 * reason resolves here.
 */
export const PROMO_REJECTION_FALLBACK_COPY = "That promo code isn't valid for this order.";

/**
 * TEAM-15229: a recurring promo code used outside its days and hours (chowly-api TEAM-15227, code 46
 * on the wire). Kept **out** of `PROMO_REJECTION_COPY` on purpose: that catalog is TEAM-14743's
 * Product-approved set of fixed sentences, while this reason's copy is built per rejection from the
 * schedule riding in the same `additional_data` (`formatPromoScheduleMessage`,
 * `@/utils/promoSchedule`). Matched on the reason alone — the numeric code is not consulted, so the
 * message works whether coo-api sends 46 or, before TEAM-15228, maps it to `0`/Unknown.
 */
export const PROMO_REJECTION_OUTSIDE_SCHEDULE_REASON = 'outside_schedule';

/**
 * The wire code chowly-api (TEAM-15227) and coo-api (TEAM-15228) give the `outside_schedule`
 * rejection. The reason above is the primary trigger; this code is consulted only when the reason is
 * unreadable (`additional_data` missing, not JSON, or carrying no reason), so the diner still reads
 * the outside-schedule fallback line rather than the generic upstream detail (TEAM-15229 AC6,
 * decided 2026-10-08). Deliberately NOT catalogued in `FPO_ERROR_COPY`, which mirrors coo-api's enum
 * only through 36 (TEAM-14743). Before TEAM-15228 ships coo-api maps 46 to `0`/Unknown, which cannot
 * be told apart from any other unknown failure, so `0` stays on the generic path.
 */
export const PROMO_OUTSIDE_SCHEDULE_WIRE_CODE = '46';

/**
 * The `outside_schedule` line when the schedule is missing, unreadable, says nothing (every day, all
 * day) or would not fit a toast. Still more useful than the generic fallback: it tells the diner the
 * code is real and the timing is the problem.
 */
export const PROMO_OUTSIDE_SCHEDULE_FALLBACK_COPY = "This code isn't valid at this time.";

/**
 * The **only** place `PROMO_REJECTION_COPY` is indexed, mirroring `lookupErrorSlugCopy`.
 *
 * The `hasOwnProperty` guard is load-bearing, not boilerplate: the key is an arbitrary wire string,
 * so a plain index reaches `Object.prototype` — `PROMO_REJECTION_COPY['toString']` is a truthy
 * function that would then be returned where a `string` is declared and handed to the toast's
 * `dangerouslySetInnerHTML`. Own properties only.
 *
 * @returns the catalogued sentence, or `undefined` for a reason this catalog does not know (which
 *   the render rule turns into `PROMO_REJECTION_FALLBACK_COPY`).
 */
export function lookupPromoRejectionCopy(reason: string | null | undefined): string | undefined {
  if (!reason || !Object.prototype.hasOwnProperty.call(PROMO_REJECTION_COPY, reason)) {
    return undefined;
  }
  return (PROMO_REJECTION_COPY as Record<string, string>)[reason];
}
